/* eslint-disable @typescript-eslint/member-ordering */
import type * as ents from '@attain-apps/entities'
import { Word } from '@attain-apps/string-builder'
import { TypeChecker } from '@attain-apps/type-guards'
import type { IDisposable } from 'monaco-editor'
import * as rxjs from 'rxjs'

import { Language } from './language'
import { CompletionProvider } from './languages'
import type { Syntax } from './syntax'

const CUSTOM_THEME_NAME = 'atn-vs-dark'

const DEFAULT_EDITOR_OPTIONS: monaco.editor.IEditorOptions &
  monaco.editor.IGlobalEditorOptions = {
  automaticLayout: true,
  minimap: {
    autohide: true,
    scale: 3,
  },
  readOnlyMessage: {
    value: 'This content is read-only',
  },
  scrollBeyondLastLine: false,
  tabSize: 2,
  theme: CUSTOM_THEME_NAME,
}

/**
 * Used for managing
 *
 * @see {@link MonacoManager.do}, {@link MonacoManager.bindTo}
 */
export class MonacoManager {
  /**
   * Tracks all instances of {@link MonacoManager}, but using {@link WeakRef} so
   * that unused instances of {@link MonacoManager} can be garbage collected
   */
  private static readonly _instanceRefs: WeakRef<MonacoManager>[] = []

  /**
   * Used by {@link _initSharedMonaco} to ensure the initialization logic is
   * only ran once
   */
  private static _monacoInitialized = false

  /**
   * Returns all existing instances of {@link MonacoManager}
   */
  private static _getInstances(): readonly MonacoManager[] {
    const instances: MonacoManager[] = []
    for (const ref of this._instanceRefs) {
      const manager = ref.deref()
      if (manager) {
        // If the reference exists, we use it
        instances.push(manager)
      } else {
        // If it does not, we remove it from our stored references
        this._instanceRefs.splice(instances.length, 1)
      }
    }
    return instances
  }

  /**
   * Variable used for cleanup of the dmsQlCompletionProvider. In case
   * where completionProvider is registered multiple times without disposing
   * we start to get multiple suggestions of the same value
   *
   * @see {@link IDisposable}
   */
  private static _dmsQlCompletionProviderDisposable?: IDisposable

  /**
   * Initializes various {@link monaco} features that are used across all
   * {@link MonacoManager}
   *
   * Will only execute once. Every subsequent call is just a no-op
   */
  private static _initSharedMonaco(): void {
    if (this._monacoInitialized) {
      return
    }
    this._monacoInitialized = true
    this._initializeCustomTheme()
    this._initSnippets()
    this._initLanguages()
  }

  /**
   * Defines a custom Monaco editor theme used across all editor instances
   *
   * The theme `atn-vs-dark` extends the default 'vs-dark' theme with custom
   * token colors for specific syntax elements like functions, keywords, and
   * delimiters used by {@link Language.DMS_QUERY}
   *
   * @see {@link monaco.editor.defineTheme}
   */
  private static _initializeCustomTheme() {
    monaco.editor.defineTheme(CUSTOM_THEME_NAME, {
      base: 'vs-dark',
      inherit: true,
      rules: [
        /* eslint-disable sort-keys */
        { token: 'function-key', foreground: '#FFA657', fontStyle: 'bold' },

        { token: 'keyword', foreground: '#CE91C8', fontStyle: 'bold' }, // keyword
        { token: 'object-key', foreground: '#9CDCFE' },
        { token: 'reference', foreground: '#7FDBCA' }, // "reference"
        { token: 'enum', foreground: '#FFE787' },

        { token: 'string', foreground: '#CE9178' },
        { token: 'number', foreground: '#B5CEA8' },
        { token: 'boolean', foreground: '#569CD6' },

        // red color indicating not valid string
        { token: 'invalid', foreground: '#F00000' },

        { token: 'delimiter.bracket', foreground: '#FFFFFF' },
        { token: 'delimiter.array', foreground: '#FFFFFF' },
        { token: 'delimiter', foreground: '#FFFFFF' },
      ],
      colors: {},
      /* eslint-enable sort-keys */
    })
  }

  /**
   * Uses {@link monaco.languages.registerCompletionItemProvider} to register a
   * single {@link monaco.languages.CompletionItemProvider} that will get the
   * appropriate snippets for each {@link monaco.editor.ITextModel}
   */
  private static _initSnippets(): void {
    // TODO: figure out a better solution for the language filter. For some
    // reason, when working in JSON, filtering on '*' means the snippets are
    // only available when typing in nested properties, whereas filtering on
    // 'json' means the snippets are available everywhere. Filtering on both is
    // a fine workaround, as it ensure snippets are available everywhere when
    // the language is json, and hopefully at least some of the places in other
    // languages. However, since this hasn't been tested against other languages
    // (as of 2/23/2025), there may be issues with other languages as well.
    // Hopefully, we can eventually find a more robust solution for all
    // languages
    monaco.languages.registerCompletionItemProvider(['*', 'json'], {
      provideCompletionItems: (model, position, context) => {
        for (const manager of this._getInstances()) {
          if (model === manager._objs?.model) {
            const word = model.getWordUntilPosition(position)
            const range: monaco.IRange = {
              endColumn: word.endColumn,
              endLineNumber: position.lineNumber,
              startColumn: word.startColumn,
              startLineNumber: position.lineNumber,
            }
            const snippets = manager._syntax?.snippets
            return {
              suggestions:
                snippets
                  ?.map(snippet =>
                    snippet.appliedTo({
                      ...context,
                      model,
                      position,
                      range,
                      word,
                    }),
                  )
                  .filter(TypeChecker.isNotNullish.raw) ?? [],
            }
          }
        }
        return { suggestions: [] }
      },
    })
  }

  /**
   * Registers Monaco editor language definitions and their associated
   * configurations including tokenizers and themes
   *
   * @see {@link monaco.languages.register},
   * {@link monaco.languages.setMonarchTokensProvider},
   * {@link monaco.editor.defineTheme}
   */
  private static _initLanguages(): void {
    const languages = Language.ALL

    for (const {
      config,
      monarch,
      name,
      documentFormattingEditProvider,
    } of languages) {
      // Register new custom language
      monaco.languages.register({ id: name })

      if (config) {
        // If Language has a configuration, register it
        monaco.languages.setLanguageConfiguration(name, config)
      }

      // If Language  has tokenizer, register it
      if (monarch) {
        monaco.languages.setMonarchTokensProvider(name, monarch)
      }

      if (documentFormattingEditProvider) {
        monaco.languages.registerDocumentFormattingEditProvider(
          name,
          documentFormattingEditProvider,
        )
      }
    }
  }

  /**
   * Uniquely identifies this {@link MonacoManager}
   */
  public readonly id = Word.generate()

  /** @see {@link MonacoObjects} */
  private _objs: MonacoObjects | undefined

  /** @see {@link bindTo} */
  private readonly _containerElem$ = new rxjs.ReplaySubject<HTMLElement>(1)

  /**
   * {@link Syntax} that is applied to this {@link MonacoManager}
   */
  public get syntax(): Syntax | undefined {
    return this._syntax
  }
  public set syntax(syntax: Syntax | undefined) {
    this._syntax = syntax
    this._applySyntax()
  }
  /** @see {@link syntax} */
  private _syntax: Syntax | undefined

  public set dmsSchema(schema: ents.core.dms.models.Schema | undefined) {
    this._dmsSchema = schema
    this._enableDmsQlCompletionProvider()
  }
  /** @see {@link dmsSchema} */
  private _dmsSchema: ents.core.dms.models.Schema | undefined

  /**
   * Used by {@link _applyToMonacoEditor} to register callbacks before
   * {@link _monacoEditor} has been created
   *
   * These are applied in {@link _initSharedMonaco}
   */
  private readonly _pendingDoers: Doer[] = []

  constructor(_dependenciesLoaded$: rxjs.Observable<void>) {
    MonacoManager._instanceRefs.push(new WeakRef(this))
    // Waits until we have a container element, and the dependencies are loaded
    // before initializing the Monaco Editor
    rxjs
      .combineLatest([this._containerElem$, _dependenciesLoaded$])
      .pipe(rxjs.first())
      .subscribe(([containerElem]) => {
        this._initInstanceMonaco(containerElem)
      })
  }

  /**
   * Attaches this {@link MonacoManager} to a particular {@link HTMLElement},
   * such that the {@link _monacoEditor} will be created in the given
   * {@link HTMLElement}
   *
   * __NOTE:__ {@link _monacoEditor} will not be created until this is called
   *
   * Should be called exactly once
   */
  public bindTo(containerElem: HTMLElement): void {
    this._containerElem$.next(containerElem)
  }

  /**
   * Used to apply updates to {@link _monacoEditor} whether or not the
   * {@link _monacoEditor} has been created yet
   */
  public do(doer: Doer): void {
    if (this._objs) {
      doer(this._objs)
    } else {
      this._pendingDoers.push(doer)
    }
  }

  /**
   * Instantiates various {@link monaco} components (namely
   * {@link MonacoObjects}) for this {@link MonacoManager}
   */
  private _initInstanceMonaco(containerElem: HTMLElement): void {
    if (this._objs) {
      // We should only initialize once, or else we might get unpredictable
      // behavior
      return
    }
    MonacoManager._initSharedMonaco()

    const model = monaco.editor.createModel(
      '',
      undefined,
      // NOTE: Using the `file:///` prefix seems to be necessary to get the file
      // matching to work properly for JSON schema validation
      monaco.Uri.parse(`file:///${this.id}`),
    )
    const editor = monaco.editor.create(containerElem, {
      ...DEFAULT_EDITOR_OPTIONS,
      model,
    })
    this._objs = { editor, model }

    // Applies registered callbacks
    for (const doer of this._pendingDoers) {
      doer(this._objs)
    }
    this._pendingDoers.length = 0
  }

  /**
   * Applies the {@link _syntax} so that it actually affects what is shown to
   * the user
   */
  private _applySyntax(): void {
    this._applyLanguage()
    this._applyJsonSchema()
    // NOTE: snippets are handled through _initSnippets
  }

  /**
   * Applies the {@link Syntax.language}
   *
   * Used by {@link _applySyntax}
   */
  private _applyLanguage(): void {
    const language = this._syntax?.language
    if (language) {
      this.do(({ model }) => {
        monaco.editor.setModelLanguage(model, language.name)
      })
    }
  }

  /**
   *
   */
  private _enableDmsQlCompletionProvider(): void {
    if (this._dmsSchema) {
      const completionProvider = new CompletionProvider(this._dmsSchema)
      this.do(() => {
        if (MonacoManager._dmsQlCompletionProviderDisposable) {
          MonacoManager._dmsQlCompletionProviderDisposable.dispose()
        }
        MonacoManager._dmsQlCompletionProviderDisposable =
          monaco.languages.registerCompletionItemProvider(
            Language.DMS_QUERY.name,
            {
              provideCompletionItems: (model, position) =>
                completionProvider.getSuggestions(model, position),
            },
          )
      })
    }
  }

  /**
   * Applies the {@link Syntax.jsonSchemaUri}
   *
   * Used by {@link _applySyntax}
   */
  private _applyJsonSchema(): void {
    this.do(() => {
      // Since we have to configure all JSON schemas at once, we need to go
      // through all current instances and group them by JSON schema
      const managerIdsBySchemaUri: Record<string, string[]> = {}
      for (const instance of MonacoManager._getInstances()) {
        const instanceJsonSchemaUri = instance._syntax?.jsonSchemaUri
        if (instanceJsonSchemaUri) {
          const managerIds = (managerIdsBySchemaUri[instanceJsonSchemaUri] ??=
            [])
          managerIds.push(instance.id)
        }
      }
      // Then we register all JSON schemas
      monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
        enableSchemaRequest: true,
        // The manager IDs are also configured as the file names
        schemas: Object.entries(managerIdsBySchemaUri).map(
          ([uri, managerIds]) => ({ fileMatch: managerIds, uri }),
        ),
        validate: true,
      })
    })
  }
}

/**
 * Contains all {@link monaco} objects created by {@link MonacoManager}
 */
interface MonacoObjects {
  readonly model: monaco.editor.ITextModel
  readonly editor: monaco.editor.IStandaloneCodeEditor
}

type Doer = (objs: MonacoObjects) => void
